// ── BCHNostr — Add Video ──────────────────────────────────────────
//
// The creator pastes a URL to a video that ALREADY EXISTS somewhere else.
// Nothing is uploaded here — there is no file input, no transcode, no
// progress bar, because BCHNostr never touches the media.
//
// The flow is deliberately one step:
//
//   paste URL → we look up the provider's own metadata → publish
//
// Metadata comes from the provider's oEmbed document where one exists
// (YouTube, PeerTube), so a typical add is: paste, confirm, publish. Every
// field remains editable, and a failed lookup just means filling the form
// in by hand — it never blocks publishing.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
  X, Loader2, Video, Link2, Check, AlertTriangle, ExternalLink,
  Sparkles, Trash2, Image as ImageIcon, BadgeCheck,
} from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { useVideoPublish } from '../hooks/useVideo';
import { useCreatorVerifications } from '../hooks/useCreatorVerification';
import { uploadImage } from '../lib/imageUpload';
import {
  VIDEO_CATEGORIES, parseHashtagInput, videoWatchPath, formatVideoDuration,
  type BCHVideo,
} from '../lib/video';
import {
  resolveVideoUrl, fetchVideoMetadata, providerLabel,
  type ResolvedVideo,
} from '../lib/videoProviders';
import {
  verifyPlatformForProvider, verifyPlatformLabel,
} from '../lib/creatorVerify';
import VerifyChannelModal from './VerifyChannelModal';

interface AddVideoModalProps {
  onClose: () => void;
  /** Called with the new/updated video's `d` tag after a successful publish. */
  onPublished?: (dTag: string) => void;
  /** When present the modal edits an existing video instead of adding one. */
  editing?: BCHVideo;
}

type LookupState = 'idle' | 'looking' | 'done' | 'failed';

/**
 * Capture a still frame from a directly-playable video URL and return it as
 * a JPEG File, so a raw .mp4 / Blossom link gets a REAL poster instead of
 * the BCHNostr logo placeholder.
 *
 * This only works for videos the browser can actually decode (same-origin
 * or CORS-enabled direct files) — YouTube/PeerTube iframes are opaque and
 * never reach here. It grabs a frame ~1s in (past any black lead-in),
 * draws it to a canvas, and encodes a JPEG. Everything is best-effort:
 * any failure (CORS-tainted canvas, decode error, timeout) rejects and the
 * caller simply keeps the placeholder — a thumbnail is a nicety, never a
 * requirement for publishing.
 */
function captureVideoFrame(videoUrl: string): Promise<File> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous'; // required or toBlob throws on a tainted canvas
    video.muted = true;
    video.playsInline = true;
    video.preload = 'metadata';
    video.src = videoUrl;

    let settled = false;
    const cleanup = () => { video.removeAttribute('src'); video.load(); };
    const fail = (msg: string) => { if (settled) return; settled = true; cleanup(); reject(new Error(msg)); };

    // Don't hang forever on a slow/dead source.
    const timeout = setTimeout(() => fail('Timed out reading the video'), 15000);

    video.onloadedmetadata = () => {
      // Seek a little past the start to skip black/intro frames, but never
      // past the end of a very short clip.
      const target = Math.min(1, (video.duration || 2) / 2);
      try { video.currentTime = target; } catch { /* some sources need a play() nudge */ }
    };

    video.onseeked = () => {
      if (settled) return;
      try {
        const w = video.videoWidth;
        const h = video.videoHeight;
        if (!w || !h) return fail('Video has no visible frame');
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) return fail('Canvas unavailable');
        ctx.drawImage(video, 0, 0, w, h);
        canvas.toBlob(
          (blob) => {
            clearTimeout(timeout);
            if (!blob) return fail('Could not encode the frame');
            settled = true;
            cleanup();
            resolve(new File([blob], 'thumbnail.jpg', { type: 'image/jpeg' }));
          },
          'image/jpeg',
          0.82,
        );
      } catch {
        // Almost always a CORS-tainted canvas — the host didn't send
        // Access-Control-Allow-Origin, so the browser forbids reading pixels.
        fail('This video host blocks automatic thumbnails (CORS). Upload one instead.');
      }
    };

    video.onerror = () => fail('Could not load the video to grab a frame');
  });
}

export default function AddVideoModal({ onClose, onPublished, editing }: AddVideoModalProps) {
  const { user, setShowLoginDialog } = useAppContext();
  const { addVideo, publishing } = useVideoPublish();

  const [url, setUrl] = useState(editing?.url || '');
  const [title, setTitle] = useState(editing?.title || '');
  const [description, setDescription] = useState(editing?.description || '');
  const [thumbnailUrl, setThumbnailUrl] = useState(editing?.thumbnailUrl || '');
  const [category, setCategory] = useState(editing?.category || 'bch');
  const [hashtags, setHashtags] = useState((editing?.hashtags || []).join(', '));
  const [duration, setDuration] = useState(editing?.duration || 0);

  const [resolved, setResolved] = useState<ResolvedVideo | null>(
    editing ? resolveVideoUrl(editing.url) : null,
  );
  const [lookup, setLookup] = useState<LookupState>(editing ? 'done' : 'idle');
  const [error, setError] = useState('');
  const [savedDTag, setSavedDTag] = useState('');
  const [uploading, setUploading] = useState(false);
  const [generatingThumb, setGeneratingThumb] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // Which channels this creator has already verified. Loaded so the
  // post-publish nudge only appears when it's actually actionable — a
  // creator already verified on this platform shouldn't be re-prompted.
  const { verifications } = useCreatorVerifications(user?.pubkey);

  // Fields the creator has typed into. Auto-fill must never overwrite
  // someone's own words.
  const touched = useRef<Set<string>>(new Set());
  const mark = (field: string) => touched.current.add(field);

  // Escape closes, and the modal owns focus while open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  /**
   * Resolve the URL and pull whatever the provider will tell us.
   *
   * Runs debounced as the creator types, because "paste and it just works"
   * is the entire point. Never throws and never blocks the form.
   */
  const runLookup = useCallback(async (raw: string) => {
    const res = resolveVideoUrl(raw);
    setResolved(res);
    if (!res) { setLookup('idle'); return; }

    // A predictable thumbnail exists for some providers even with no
    // metadata endpoint — use it immediately.
    if (res.thumbnailUrl && !touched.current.has('thumbnailUrl')) {
      setThumbnailUrl(res.thumbnailUrl);
    }

    setLookup('looking');
    const meta = await fetchVideoMetadata(raw);

    if (meta) {
      if (meta.title && !touched.current.has('title')) setTitle(meta.title);
      if (meta.description && !touched.current.has('description')) setDescription(meta.description);
      if (meta.thumbnailUrl && !touched.current.has('thumbnailUrl')) setThumbnailUrl(meta.thumbnailUrl);
      if (meta.duration > 0 && !touched.current.has('duration')) setDuration(meta.duration);
    }
    setLookup(meta ? 'done' : 'failed');

    // For a directly-playable video (raw .mp4 / Blossom link) there is no
    // provider poster, so the card would otherwise show the BCHNostr logo.
    // Auto-grab a real frame from the video itself — silently, best-effort.
    if (
      res.playback === 'file' &&
      res.fileUrl &&
      !touched.current.has('thumbnailUrl') &&
      !(meta?.thumbnailUrl) &&
      !res.thumbnailUrl
    ) {
      generateThumbnail(res.fileUrl, true);
    }
  }, [generateThumbnail]);

  useEffect(() => {
    if (editing) return;
    const value = url.trim();
    if (!value) { setResolved(null); setLookup('idle'); return; }
    const timer = setTimeout(() => { runLookup(value); }, 600);
    return () => clearTimeout(timer);
  }, [url, editing, runLookup]);

  // Grab a poster frame from a directly-playable video and upload it as the
  // thumbnail. Only meaningful for `file` playback (raw .mp4 / Blossom URLs);
  // iframe providers (YouTube etc.) are opaque and their own poster is used.
  const generateThumbnail = useCallback(async (videoUrl: string, silent: boolean) => {
    setGeneratingThumb(true);
    if (!silent) setError('');
    try {
      const frame = await captureVideoFrame(videoUrl);
      const uploaded = await uploadImage(frame);
      mark('thumbnailUrl');
      setThumbnailUrl(uploaded);
    } catch (err) {
      // Auto attempts fail quietly — the placeholder is a fine fallback and
      // the user can always upload their own. Manual attempts surface why.
      if (!silent) setError(err instanceof Error ? err.message : 'Could not generate a thumbnail');
    } finally {
      setGeneratingThumb(false);
    }
  }, []);

  const handleThumbnailFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      mark('thumbnailUrl');
      // A THUMBNAIL, not a video. Images are small and this is the app's
      // existing image host — no video ever goes through here.
      setThumbnailUrl(await uploadImage(file));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Thumbnail upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handlePublish = async () => {
    if (!user) { setShowLoginDialog(true); return; }
    if (!resolved) {
      setError('Paste a link to a video on YouTube, Odysee, PeerTube, or a direct video URL.');
      return;
    }
    setError('');
    try {
      const dTag = await addVideo({
        url,
        title,
        description,
        thumbnailUrl,
        duration,
        category,
        hashtags: parseHashtagInput(hashtags),
        // Editing preserves the original publication date, so a metadata
        // fix doesn't relaunch an old video to the top of every feed.
        publishedAt: editing?.publishedAt,
      });
      setSavedDTag(dTag);
      onPublished?.(dTag);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to publish video');
    }
  };

  const canPublish = !!resolved && !!title.trim() && !publishing;

  // ── Logged out ──
  if (!user) {
    return createPortal(
      <Backdrop onClose={onClose}>
        <Panel>
          <Header title="Add a video" onClose={onClose} />
          <div className="px-5 pb-5 text-center space-y-3">
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
              <Video className="w-7 h-7 text-primary" />
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Log in with your Nostr key to add a video. Your existing profile becomes your
              channel — there's no separate video account.
            </p>
            <button
              onClick={() => { setShowLoginDialog(true); onClose(); }}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold"
            >
              Log in
            </button>
          </div>
        </Panel>
      </Backdrop>,
      document.body,
    );
  }

  // ── Success ──
  if (savedDTag) {
    // Should we nudge the creator to verify this channel? Only when the
    // video is on a verifiable platform (YouTube/Odysee) AND they haven't
    // already verified a channel on that platform. Verification is what
    // unlocks tips on YouTube/Odysee videos, so this is the natural moment.
    const platform = resolved ? verifyPlatformForProvider(resolved.provider) : null;
    const alreadyVerifiedPlatform = platform
      ? verifications.some(v => v.platform === platform)
      : false;
    const showVerifyNudge = !editing && !!platform && !alreadyVerifiedPlatform;

    return createPortal(
      <>
      <Backdrop onClose={verifyOpen ? () => {} : onClose}>
        <Panel>
          <Header title={editing ? 'Video updated' : 'Video added'} onClose={onClose} />
          <div className="px-5 pb-5 text-center space-y-3">
            <div className="w-14 h-14 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto">
              <Check className="w-7 h-7 text-emerald-500" />
            </div>
            <p className="text-sm font-bold text-emerald-500">
              {editing ? 'Changes published' : 'Your video is live on BCHNostr'}
            </p>
            <p className="text-xs text-muted-foreground leading-relaxed">
              It now has a page with discussion, tips and boosts. The video itself keeps
              streaming from {resolved ? providerLabel(resolved.provider) : 'its host'} —
              BCHNostr never stores or re-uploads it.
            </p>

            {/* ── Verify-to-earn-tips nudge (YouTube / Odysee only) ── */}
            {showVerifyNudge && (
              <div className="rounded-xl border border-primary/30 bg-primary/5 p-3.5 text-left space-y-2">
                <p className="flex items-center gap-1.5 text-xs font-bold text-primary">
                  <BadgeCheck className="w-4 h-4 shrink-0" />
                  Want tips on your {platform ? verifyPlatformLabel(platform) : ''} videos?
                </p>
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  Tips are off on {platform ? verifyPlatformLabel(platform) : ''} videos until you
                  prove you own the channel — so nobody can farm tips off a video they only shared.
                  It's a one-time check per channel and takes a minute.
                </p>
                <button
                  onClick={() => setVerifyOpen(true)}
                  className="w-full py-2 rounded-lg bg-primary text-primary-foreground text-xs font-semibold flex items-center justify-center gap-1.5 hover:bg-primary/90"
                >
                  <BadgeCheck className="w-3.5 h-3.5" /> Verify your channel
                </button>
              </div>
            )}

            <div className="flex gap-2 justify-center">
              <Link
                to={videoWatchPath(user.pubkey, savedDTag)}
                onClick={onClose}
                className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold"
              >
                Open video
              </Link>
              <button onClick={onClose} className="px-4 py-2 rounded-lg bg-muted text-sm font-medium">
                {showVerifyNudge ? 'Maybe later' : 'Done'}
              </button>
            </div>
          </div>
        </Panel>
      </Backdrop>

      {verifyOpen && (
        <VerifyChannelModal
          initialVideoUrl={url}
          onClose={() => setVerifyOpen(false)}
          onVerified={() => setVerifyOpen(false)}
        />
      )}
      </>,
      document.body,
    );
  }

  return createPortal(
    <Backdrop onClose={onClose}>
      <Panel>
        <Header title={editing ? 'Edit video' : 'Add a video'} onClose={onClose} />

        <div className="px-5 pb-5 space-y-3.5 max-h-[80vh] overflow-y-auto">
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            Paste a link to a video that's already online. BCHNostr doesn't host, upload,
            or re-encode video — it adds discovery, discussion and BCH payments around
            the video where it already lives.
          </p>

          {/* ── URL ── */}
          <div>
            <label className="text-[11px] font-medium text-muted-foreground flex items-center gap-1.5">
              <Link2 className="w-3 h-3" /> Video URL <span className="text-primary">*</span>
            </label>
            <input
              value={url}
              onChange={e => { mark('url'); setUrl(e.target.value); }}
              placeholder="https://www.youtube.com/watch?v=…"
              maxLength={600}
              inputMode="url"
              autoComplete="off"
              spellCheck={false}
              autoFocus={!editing}
              className="w-full bg-background rounded-lg px-3 py-2 text-sm font-mono mt-1 focus:outline-none focus:ring-1 focus:ring-primary"
            />

            {/* Provider feedback */}
            {lookup === 'looking' && (
              <p className="flex items-center gap-1.5 text-[10px] text-muted-foreground mt-1.5">
                <Loader2 className="w-3 h-3 animate-spin" /> Reading details from the provider…
              </p>
            )}
            {resolved && lookup !== 'looking' && (
              <div className="mt-1.5 space-y-1">
                <p className="flex items-center gap-1.5 text-[10px] font-medium text-primary">
                  <Check className="w-3 h-3 shrink-0" />
                  {providerLabel(resolved.provider)} detected
                  {resolved.playback === 'iframe' && ' · plays in-page'}
                  {resolved.playback === 'file' && ' · direct video file'}
                </p>
                {resolved.playback === 'link' && (
                  <p className="flex items-start gap-1.5 text-[10px] text-amber-600 dark:text-amber-400">
                    <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
                    <span>
                      We can't safely embed this page, so the video page will link out to it
                      instead. Discussion, tips and boosts still work.
                    </span>
                  </p>
                )}
                {lookup === 'failed' && (
                  <p className="text-[10px] text-muted-foreground">
                    Couldn't read the title automatically — fill it in below.
                  </p>
                )}
              </div>
            )}
            {!resolved && url.trim().length > 4 && lookup !== 'looking' && (
              <p className="flex items-start gap-1.5 text-[10px] text-red-500 mt-1.5">
                <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
                <span>That doesn't look like a video link. Use an https URL.</span>
              </p>
            )}
            {!url.trim() && (
              <p className="text-[10px] text-muted-foreground mt-1">
                YouTube, Odysee, PeerTube, or a direct .mp4 / .webm / Blossom URL.
              </p>
            )}
          </div>

          {/* ── Title ── */}
          <div>
            <label className="text-[11px] font-medium text-muted-foreground">
              Title <span className="text-primary">*</span>
            </label>
            <input
              value={title}
              onChange={e => { mark('title'); setTitle(e.target.value); }}
              placeholder="What's this video about?"
              maxLength={300}
              className="w-full bg-background rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {/* ── Description ── */}
          <div>
            <label className="text-[11px] font-medium text-muted-foreground">Description</label>
            <textarea
              value={description}
              onChange={e => { mark('description'); setDescription(e.target.value); }}
              placeholder="Optional. Plain text."
              rows={3}
              maxLength={5000}
              className="w-full bg-background rounded-lg px-3 py-2 text-sm mt-1 resize-none focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {/* ── Thumbnail ── */}
          <div>
            <label className="text-[11px] font-medium text-muted-foreground flex items-center gap-1.5">
              <ImageIcon className="w-3 h-3" /> Thumbnail
            </label>
            <div className="flex items-start gap-3 mt-1">
              {thumbnailUrl ? (
                <img
                  src={thumbnailUrl}
                  alt=""
                  className="w-24 aspect-video rounded-lg object-cover border border-border shrink-0 bg-muted"
                />
              ) : (
                <div className="w-24 aspect-video rounded-lg bg-muted border border-dashed border-border flex items-center justify-center shrink-0">
                  {generatingThumb
                    ? <Loader2 className="w-4 h-4 text-primary animate-spin" />
                    : <Video className="w-4 h-4 text-muted-foreground/50" />}
                </div>
              )}
              <div className="flex-1 min-w-0 space-y-1.5">
                <input
                  value={thumbnailUrl}
                  onChange={e => { mark('thumbnailUrl'); setThumbnailUrl(e.target.value); }}
                  placeholder="https://…/thumb.jpg"
                  maxLength={600}
                  className="w-full bg-background rounded-lg px-3 py-1.5 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading || generatingThumb}
                    className="px-2.5 py-1 rounded-lg bg-muted text-[11px] font-medium hover:bg-muted/70 disabled:opacity-50 flex items-center gap-1"
                  >
                    {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                    {uploading ? 'Uploading' : 'Upload image'}
                  </button>
                  {resolved?.playback === 'file' && resolved.fileUrl && (
                    <button
                      type="button"
                      onClick={() => generateThumbnail(resolved.fileUrl, false)}
                      disabled={uploading || generatingThumb}
                      className="px-2.5 py-1 rounded-lg bg-primary/10 text-primary text-[11px] font-medium hover:bg-primary/20 disabled:opacity-50 flex items-center gap-1"
                      title="Capture a frame from the video as the thumbnail"
                    >
                      {generatingThumb ? <Loader2 className="w-3 h-3 animate-spin" /> : <Video className="w-3 h-3" />}
                      {generatingThumb ? 'Grabbing frame' : 'Grab from video'}
                    </button>
                  )}
                  {thumbnailUrl && (
                    <button
                      type="button"
                      onClick={() => { mark('thumbnailUrl'); setThumbnailUrl(''); }}
                      className="p-1 rounded text-muted-foreground hover:text-red-500"
                      title="Remove thumbnail"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
                <p className="text-[10px] text-muted-foreground">
                  {resolved?.playback === 'file'
                    ? 'Auto-captured from the video when possible. Upload your own to override. Images only — never the video file.'
                    : 'Usually filled in automatically. Images only — never the video file.'}
                </p>
              </div>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={handleThumbnailFile}
              className="hidden"
            />
          </div>

          {/* ── Category + duration ── */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-medium text-muted-foreground">Category</label>
              <select
                value={category}
                onChange={e => { mark('category'); setCategory(e.target.value); }}
                className="w-full bg-background rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-1 focus:ring-primary"
              >
                {VIDEO_CATEGORIES.map(c => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-medium text-muted-foreground">
                Length <span className="text-muted-foreground/60">(seconds)</span>
              </label>
              <input
                type="number"
                min={0}
                value={duration || ''}
                onChange={e => {
                  mark('duration');
                  const n = parseInt(e.target.value, 10);
                  setDuration(Number.isFinite(n) && n > 0 ? n : 0);
                }}
                placeholder="Optional"
                className="w-full bg-background rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-1 focus:ring-primary"
              />
              {duration > 0 && (
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  {formatVideoDuration(duration)}
                </p>
              )}
            </div>
          </div>

          {/* ── Tags ── */}
          <div>
            <label className="text-[11px] font-medium text-muted-foreground">Tags</label>
            <input
              value={hashtags}
              onChange={e => { mark('hashtags'); setHashtags(e.target.value); }}
              placeholder="bch, cashtokens, tutorial"
              maxLength={300}
              className="w-full bg-background rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <p className="text-[10px] text-muted-foreground mt-0.5">
              Comma-separated. Helps people find the video in search.
            </p>
          </div>

          {/* ── Publish ── */}
          <button
            onClick={handlePublish}
            disabled={!canPublish}
            className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-bold text-sm flex items-center justify-center gap-2 hover:bg-primary/90 disabled:opacity-50"
          >
            {publishing
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <Sparkles className="w-4 h-4" />}
            {publishing ? 'Publishing…' : editing ? 'Save changes' : 'Add video'}
          </button>

          {error && <p className="text-xs text-red-500 text-center">{error}</p>}

          {resolved && (
            <a
              href={resolved.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-1.5 text-[10px] text-muted-foreground hover:text-primary"
            >
              Check the original on {providerLabel(resolved.provider)}
              <ExternalLink className="w-2.5 h-2.5" />
            </a>
          )}

          <p className="text-[10px] text-muted-foreground/70 text-center leading-relaxed">
            Only a small metadata note is published to Nostr — a title, a link, and a
            thumbnail URL. The video file stays where you host it.
          </p>
        </div>
      </Panel>
    </Backdrop>,
    document.body,
  );
}

// ── Chrome ────────────────────────────────────────────────────────

function Backdrop({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      {children}
    </div>
  );
}

function Panel({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl bg-popover border border-border shadow-2xl overflow-hidden"
      onClick={e => e.stopPropagation()}
    >
      {children}
    </div>
  );
}

function Header({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <div className="flex items-center justify-between px-5 pt-5 pb-3">
      <h2 className="font-bold text-base flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-emerald-600 flex items-center justify-center">
          <Video className="w-4 h-4 text-white" />
        </div>
        {title}
      </h2>
      <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
